import './style.css'

import logo from '../../assets/images/cadre1/logoEcoleDavid.png'
import mailIcon from '../../assets/images/cadre1/email.png'
import Horloge from '../../assets/images/cadre1/Horloge.png'
import Lieu from '../../assets/images/cadre1/Lieu.png'
import Phone from '../../assets/images/cadre1/phone-call.png'

import { useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { storage } from '../../firebase.config'
import { getDownloadURL, ref } from 'firebase/storage'

function ObjectPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [hdUrl, setHdUrl] = useState(null)

  const object = location.state?.data
  const horaires = location.state?.horaires || {}
  const url = location.state?.url
  const prevPrix = location.state?.prix
  const prevCat = location.state?.cat

  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [location.key])

  // Charger l'image HD directement à l'ouverture
  useEffect(() => {
    let mounted = true
    
    async function loadHdImage() {
      if (!object?.id) return
      try {
        const imageUrl = await getDownloadURL(ref(storage, `images/${object.id}`))
        if (mounted) setHdUrl(imageUrl)
      } catch (e) {
        // Fallback sur l'URL passée en state
        if (mounted) setHdUrl(url)
      }
    }
    
    loadHdImage()
    return () => { mounted = false }
  }, [object?.id, url])

  const prixAffiche = useMemo(() => {
    const v = object?.prixFix != null ? object?.prixFix : object?.prix
    return v != null ? `${v} €` : ''
  }, [object])

  /**
   * 🎨 SYSTÈME DE PARSING DE DESCRIPTION PRODUIT
   * 
   * Langage de balisage personnalisé pour affichage des descriptions de guitares
   * 
   * RÈGLES DE SYNTAXE :
   * ──────────────────────────────────────────────────────────────
   * 
   * 1️⃣ SÉPARATEUR DE BLOCS : %
   *    Chaque bloc commence et se termine par %
   *    Exemple : % Mon texte %
   * 
   * 2️⃣ TITRE PRINCIPAL : %#
   *    % # Titre principal %
   *    → Génère un <h3> avec classe "customTitle"
   * 
   * 3️⃣ PARAGRAPHE COLORÉ : @couleur{texte}
   *    % @white{Mon texte en blanc} %
   *    → Génère un <p> avec <span style="color: white">
   *    → Couleurs supportées : white, orange, blue, red, green, etc.
   *    → Couleur par défaut : orange si couleur non spécifiée ou invalide
   * 
   * 4️⃣ PARAGRAPHE NORMAL : 
   *    % Mon texte %
   *    → Génère un <p> avec couleur orange par défaut
   *    → Tout texte sans commande @couleur est affiché en orange
   * 
   * 5️⃣ TEXTE EN GRAS : *
   *    % *Texte en gras* %
   *    → Génère un <p> avec <b className="customBold"> en orange
   * 
   * 6️⃣ ESPACEMENT VERTICAL : %%
   *    % %
   *    → Bloc vide ignoré (crée un espacement naturel)
   * 
   * SÉCURITÉ :
   * ──────────────────────────────────────────────────────────────
   * ✅ Détection des accolades mal fermées
   * ✅ Fallback en cas de commande invalide
   * ✅ Protection contre les injections HTML
   * ✅ Log des erreurs de parsing en développement
   * 
   * EXEMPLES :
   * ──────────────────────────────────────────────────────────────
   * %# Americana Faded Black - CW QIT %
   * % Corps et Finition  %
   * % @white{La Americana présente une table en épicéa massif...} %
   */
  function formatDescription(text = '') {
    const finalText = []
    const newText = String(text).split('%')
    const isDev = process.env.NODE_ENV === 'development'

    // Couleurs CSS valides supportées
    const validColors = [
      'white', 'black', 'red', 'blue', 'green', 'yellow', 'orange', 'purple',
      'pink', 'brown', 'gray', 'grey', 'cyan', 'magenta', 'lime', 'navy',
      'teal', 'silver', 'gold', 'violet', 'indigo', 'maroon', 'olive'
    ]

    // Fonction utilitaire pour logger les erreurs en dev
    const logParsingError = (error, block, index) => {
      if (isDev) {
        console.warn(`⚠️ Parsing error at block ${index}:`, error, '\nBlock:', block)
      }
    }

    // Fonction pour vérifier si les accolades sont bien fermées
    const hasBalancedBraces = (str) => {
      let count = 0
      for (const char of str) {
        if (char === '{') count++
        if (char === '}') count--
        if (count < 0) return false
      }
      return count === 0
    }

    // Fonction pour valider et obtenir la couleur (avec fallback orange)
    const getValidColor = (color) => {
      const lowerColor = color?.toLowerCase()
      if (!lowerColor || !validColors.includes(lowerColor)) {
        if (isDev && lowerColor) {
          console.warn(`⚠️ Couleur "${color}" non valide, utilisation de orange par défaut`)
        }
        return 'orange'
      }
      return lowerColor
    }

    for (let i = 0; i < newText.length; i++) {
      const line = newText[i].trim()
      
      // Ignorer les blocs vides (espacement vertical)
      if (line === '') {
        continue
      }

      try {
        // 🎯 RÈGLE 1 : Titre principal avec #
        if (line.includes('#')) {
          const titleText = line.replace('#', '').trim()
          finalText.push(
            <h3 className="customTitle" key={`title-${i}`}>
              {titleText}
            </h3>
          )
        }
        // 🎯 RÈGLE 2 : Commande de couleur @color{text}
        else if (line.includes('@')) {
          // Regex améliorée pour capturer la couleur et le texte
          const colorMatches = line.match(/@(\w+)\{([^}]*)\}/)
          
          if (colorMatches) {
            const [, colorRaw, coloredText] = colorMatches
            
            // Vérifier les accolades
            if (!hasBalancedBraces(line)) {
              logParsingError('Unbalanced braces in color command', line, i)
              // Fallback : afficher le texte en orange par défaut
              finalText.push(
                <p className="customParagraph" key={`fallback-${i}`}>
                  <span style={{ color: 'orange' }}>{line}</span>
                </p>
              )
            } else {
              // Valider la couleur (avec fallback orange si invalide)
              const validatedColor = getValidColor(colorRaw)
              
              // Texte restant après la commande de couleur
              const remainingText = line.slice(colorMatches.index + colorMatches[0].length).trim()
              
              finalText.push(
                <p className="customParagraph" key={`color-${i}`}>
                  <span style={{ color: validatedColor }}>{coloredText}</span>
                  {remainingText && ` ${remainingText}`}
                </p>
              )
            }
          } else {
            logParsingError('Invalid color command syntax', line, i)
            // Fallback : afficher en orange par défaut
            finalText.push(
              <p className="customParagraph" key={`fallback-color-${i}`}>
                <span style={{ color: 'orange' }}>{line}</span>
              </p>
            )
          }
        }
        // 🎯 RÈGLE 3 : Texte en gras avec *
        else if (line.includes('*')) {
          const boldText = line.split('*')[1]?.trim()
          if (boldText) {
            finalText.push(
              <p className="customParagraph" key={`bold-${i}`}>
                <b className="customBold" style={{ color: 'orange' }}>{boldText}</b>
              </p>
            )
          }
        }
        // 🎯 RÈGLE 4 : Paragraphe normal - en orange par défaut
        else {
          finalText.push(
            <p className="customParagraph" key={`para-${i}`}>
              <span style={{ color: 'orange' }}>{line}</span>
            </p>
          )
        }
      } catch (error) {
        logParsingError(error.message, line, i)
        // Fallback ultime : afficher le texte en orange par défaut
        finalText.push(
          <p className="customParagraph" key={`error-${i}`}>
            <span style={{ color: 'orange' }}>{line}</span>
          </p>
        )
      }
    }

    return finalText
  }

  if (!object) {
    return (
      <div className="ObjPage">
        <div className="ObjContainer">
          <div className="ObjCard">
            <h1>Annonce introuvable</h1>
            <button className="ObjBtn" onClick={() => navigate(`/produits?cat=${prevCat}&prix=${prevPrix.replace('+', '%2B')}`)}>Retour au shop</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ObjPage">
      {/* TOP BAR */}
      <header className="ObjTop">
        <div className="ObjTop__inner">
          <button
            className="ObjBack"
            onClick={() => navigate(`/produits?cat=${prevCat}&prix=${prevPrix.replace('+', '%2B').replace('+', '%2B')}`)}
          >
            ← Retour au shop
          </button>

          <div className="ObjBrand">
            <img src={logo} alt="Guitare & Basse" />
          </div>

          <div className="ObjActions">
            <a className="ObjBtn ObjBtn--ghost" href="tel:0663460105">📞 Appeler</a>
            <a className="ObjBtn" href="mailto:david.foucard@gmail.com">✉️ Mail</a>
          </div>
        </div>
        <div className="ObjTop__line" />
      </header>

      <main className="ObjContainer">
        {/* HERO CARD */}
        <section className="ObjHero">
          <div className="ObjMedia">
            <div className="ObjImageWrap">
              <img className="ObjImage" src={hdUrl || url} alt={`Photo de ${object.nom}`} />
              {prixAffiche && <div className="ObjPriceBadge">{prixAffiche}</div>}
            </div>
          </div>

          <div className="ObjSide">
            <div className="ObjTitleBlock">
              <h1 className="ObjTitle">{object.nom}</h1>
              <p className="ObjModel">{object.model}</p>

              <div className="ObjMetaRow">
                {object.categorie ? <span className="ObjChip">{object.categorie}</span> : null}
                {object.etat ? <span className="ObjChip ObjChip--orange">État : {object.etat}</span> : null}
              </div>
            </div>

            <div className="ObjInfoCards">
              <a className="InfoCard" href="tel:0663460105">
                <img src={Phone} alt="" />
                <div>
                  <div className="InfoCard__title">Téléphone</div>
                  <div className="InfoCard__value">06 63 46 01 05</div>
                </div>
              </a>

              <a className="InfoCard" href="mailto:david.foucard@gmail.com">
                <img src={mailIcon} alt="" />
                <div>
                  <div className="InfoCard__title">Email</div>
                  <div className="InfoCard__value">david.foucard@gmail.com</div>
                </div>
              </a>

              <a
                className="InfoCard"
                href="https://maps.app.goo.gl/7Fu7xmWCVnfqsDpQ7"
                target="_blank"
                rel="noreferrer"
              >
                <img src={Lieu} alt="" />
                <div>
                  <div className="InfoCard__title">Adresse</div>
                  <div className="InfoCard__value">12 Rue Pasteur, 71100 Chalon-sur-Saône</div>
                </div>
              </a>

              <div className="InfoCard InfoCard--hours">
                <img src={Horloge} alt="" />
                <div>
                  <div className="InfoCard__title">Horaires</div>
                  <div className="HoursGrid">
                    <div>Mardi</div><div>{horaires['mardi']}</div>
                    <div>Mercredi</div><div>{horaires['mercredi']}</div>
                    <div>Vendredi</div><div>{horaires['vendredi']}</div>
                    <div>Samedi</div><div>{horaires['samedi']}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* DESCRIPTION */}
        <section className="ObjDesc">
          <h2 className="ObjSectionTitle">Description</h2>
          <div className="ObjDesc__content">
            {formatDescription(object.description)}
          </div>
        </section>

        {/* INFO CARDS BOTTOM (mobile only) */}
        <section className="ObjInfoCardsBottom">
          <a className="InfoCard" href="tel:0663460105">
            <img src={Phone} alt="" />
            <div>
              <div className="InfoCard__title">Téléphone</div>
              <div className="InfoCard__value">06 63 46 01 05</div>
            </div>
          </a>

          <a className="InfoCard" href="mailto:david.foucard@gmail.com">
            <img src={mailIcon} alt="" />
            <div>
              <div className="InfoCard__title">Email</div>
              <div className="InfoCard__value">david.foucard@gmail.com</div>
            </div>
          </a>

          <a
            className="InfoCard"
            href="https://maps.app.goo.gl/7Fu7xmWCVnfqsDpQ7"
            target="_blank"
            rel="noreferrer"
          >
            <img src={Lieu} alt="" />
            <div>
              <div className="InfoCard__title">Adresse</div>
              <div className="InfoCard__value">12 Rue Pasteur, 71100 Chalon-sur-Saône</div>
            </div>
          </a>

          <div className="InfoCard InfoCard--hours">
            <img src={Horloge} alt="" />
            <div>
              <div className="InfoCard__title">Horaires</div>
              <div className="HoursGrid">
                <div>Mardi</div><div>{horaires['mardi']}</div>
                <div>Mercredi</div><div>{horaires['mercredi']}</div>
                <div>Vendredi</div><div>{horaires['vendredi']}</div>
                <div>Samedi</div><div>{horaires['samedi']}</div>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER LOGO */}
        <footer className="ObjFooter">
          <img src={logo} alt="Guitare & Basse" />
        </footer>
      </main>
    </div>
  )
}

export default ObjectPage
